import React, { FC } from "react"
import {
    FilterExpression,
    isMultipleFilter,
    isBinaryFilter,
    isUnaryFilter,
    isFeedbackMessageFilter
} from "../../../Models/Client/FilterExpression"
import { FilterFields } from "./FilterComposer"
import { alphabetical } from "../../../Helpers/Functions/string"
import {
    isMultiplePredicate,
    isUnaryPredicate,
    isBinaryPredicate,
    isFeedbackMessagePredicate
} from "./FilterPredicate"
import { ExclusiveChoiceDropdown } from "dash-styled"
import { setFieldName, setPredicate } from "./FilterPredicate"
import { FilterOption } from "./FilterOption"
import { RemoveButton } from "../Button/RemoveButton"

interface FieldFilterProps<M> {
    compact: boolean
    filter: FilterExpression<M>
    fields: FilterFields<M>
    availableFields: (keyof M)[]
    className?: string
    isFirstItem: boolean
    isLastItem: boolean
    onChange: (value?: FilterExpression<M>) => void
}

const And: FC<{ isPlaceholder: boolean; className?: string }> = ({
    isPlaceholder,
    className
}) => (
    <span
        role={isPlaceholder ? "presentation" : undefined}
        className={`${className ?? ""} mr-2 ${
            isPlaceholder ? "invisible" : ""
        }`}
    >
        and
    </span>
)

export function FieldFilter<M>({
    compact,
    filter,
    fields,
    className,
    availableFields,
    isFirstItem,
    isLastItem,
    onChange
}: FieldFilterProps<M>) {
    type V = M[keyof M]
    const field = fields[filter.fieldName]

    if (field === undefined) {
        return null
    }

    const fieldNames = (Object.keys(fields) as (keyof M)[])
        .filter(
            fieldName =>
                availableFields.includes(fieldName) ||
                filter.fieldName === fieldName
        )
        .sort((one: keyof M, another: keyof M) => {
            const o = fields[one]
            const a = fields[another]

            return o !== undefined && a !== undefined
                ? alphabetical(o.label, a.label)
                : 0
        }) as (keyof M)[]
    const handleRemove = () => onChange(undefined)
    const handleChangeUnary = (value?: V) => onChange({ ...filter, value })
    const handleChangeFeedbackMessage = (value?: V) =>
        onChange({ ...filter, value })
    const handleChangeBinary = (value?: [V, V]) =>
        onChange({ ...filter, value })
    const handleChangeMultipleValue = (value?: V[]) =>
        onChange({ ...filter, value })
    const handleChangeFieldName = (index: number) => {
        const fieldName = fieldNames[index]
        const newField: FilterOption<V> | undefined = fields[fieldName]
        const predicate =
            newField === undefined ? undefined : newField.predicates[0]

        if (predicate !== undefined) {
            onChange(setFieldName(predicate, fieldName))
        }
    }
    const handleChangeOperator = (index: number) => {
        const predicate = field.predicates[index]

        if (predicate !== undefined) {
            onChange(setPredicate(filter, predicate))
        }
    }
    const renderInput = () => {
        const predicate = field.predicates.find(
            p => p.operator === filter.operator
        )

        if (predicate === undefined) {
            return null
        }

        return isMultiplePredicate(predicate)
            ? predicate.component({
                  value: isMultipleFilter(filter)
                      ? filter.value
                      : predicate.defaultValue,
                  onChange: handleChangeMultipleValue
              })
            : isUnaryPredicate(predicate)
            ? predicate.component({
                  value: isUnaryFilter(filter)
                      ? filter.value
                      : predicate.defaultValue,
                  onChange: handleChangeUnary
              })
            : isFeedbackMessagePredicate(predicate)
            ? predicate.component?.({
                  value: isFeedbackMessageFilter(filter)
                      ? filter.value
                      : predicate.defaultValue,
                  onChange: handleChangeFeedbackMessage
              })
            : isBinaryPredicate(predicate)
            ? predicate.component({
                  value: isBinaryFilter(filter)
                      ? filter.value
                      : predicate.defaultValue,
                  onChange: handleChangeBinary
              })
            : null
    }
    const fieldChooser = () => (
        <ExclusiveChoiceDropdown
            testId="filter-field-name"
            choices={fieldNames.map(f => {
                const o = fields[f]

                return o === undefined ? String(f) : o.label
            })}
            selectedIndex={fieldNames.findIndex(
                name => name === filter.fieldName
            )}
            className={`mr-2 ${
                compact ? "w-48 flex-grow-0 flex-shrink-0" : "flex-grow"
            }`}
            size="sm"
            onClick={handleChangeFieldName}
        />
    )
    const operatorChooser = () =>
        compact && field.predicates.length < 2 ? null : (
            <ExclusiveChoiceDropdown
                testId="filter-operator"
                choices={field.predicates.map(({ label }) => label)}
                selectedIndex={field.predicates.findIndex(
                    ({ operator }) => operator === filter.operator
                )}
                className="flex-grow"
                size="sm"
                onClick={handleChangeOperator}
            />
        )
    const cls = `flex justify-between items-start ${className ?? ""}`

    if (compact) {
        return (
            <div data-testid="filter-condition" className={cls}>
                <And className="my-2" isPlaceholder={isFirstItem} />
                {fieldChooser()}
                {operatorChooser()}
                {renderInput()}
                <RemoveButton
                    className="ml-2 text-grape-900"
                    icon="close"
                    onRemove={handleRemove}
                />
            </div>
        )
    }

    return (
        <div data-testid="filter-condition" className={cls}>
            <div className="flex-grow">
                <div className="flex items-center justify-stretch mb-2">
                    {fieldChooser()}
                    {operatorChooser()}
                </div>
                {renderInput()}
                {isLastItem ? null : (
                    <p className="text-xs text-gray-500 text-center my-2">
                        and
                    </p>
                )}
            </div>
            <RemoveButton
                className="ml-2 text-gray-500"
                icon="trash"
                testId="remove-filter-button"
                onRemove={handleRemove}
            />
        </div>
    )
}
